// utils/API/BackendHelper/COLLEGES/student.js

import ApiHelper from '../../ApiHelper';
import API_ENDPOINTS from '../../endpoints';

class CollegeStudentHelper {
  /**
   * Fetch all students for the COLLEGES
   * @param {Object} params - Query parameters (page, limit, search, etc.)
   * @returns {Promise} API response
   */
  async getAllStudents(params = {}) {
    try {
      const response = await ApiHelper.getPaginated(API_ENDPOINTS.COLLEGES.STUDENTS, params);
      return response;
    } catch (error) {
      throw new Error(`Failed to fetch students: ${error.message}`);
    }
  }

  /**
   * Create a single student
   * @param {Object} studentData - Student information
   * @returns {Promise} API response
   */
  async createStudent(studentData) {
    try {
      const response = await ApiHelper.post(API_ENDPOINTS.COLLEGES.STUDENTS, studentData);
      return response;
    } catch (error) {
      throw new Error(`Failed to create student: ${error.message}`);
    }
  }

  /**
   * Upload students via Excel file
   * @param {FormData} formData - Form data containing the Excel file
   * @returns {Promise} API response
   */
  async uploadStudents(formData) {
    try {
      const response = await ApiHelper.upload(
        API_ENDPOINTS.COLLEGES.STUDENTS_BULK_UPLOAD,
        formData
      );
      return response;
    } catch (error) {
      throw new Error(`Failed to upload students: ${error.message}`);
    }
  }

  /**
   * Download student upload template
   * @returns {Promise} Blob response
   */
  async downloadTemplate() {
    try {
      const response = await fetch(API_ENDPOINTS.COLLEGES.STUDENTS_TEMPLATE, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('authToken')}`,
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      return await response.blob();
    } catch (error) {
      throw new Error(`Failed to download template: ${error.message}`);
    }
  }

  /**
   * Get students progress
   * @param {Object} params - Query parameters (courseId, etc.)
   * @returns {Promise} API response
   */
  async getStudentsProgress(params = {}) {
    try {
      const response = await ApiHelper.get(API_ENDPOINTS.COLLEGES.STUDENTS_PROGRESS, params);
      return response;
    } catch (error) {
      throw new Error(`Failed to fetch students progress: ${error.message}`);
    }
  }

  /**
   * Retry sending credentials via SMS
   * @param {Array} studentIds - Array of student IDs
   * @returns {Promise} API response
   */
  async retryCredentialsSMS(studentIds) {
    try {
      const response = await ApiHelper.post(API_ENDPOINTS.COLLEGES.STUDENTS_RETRY_SMS, {
        studentIds
      });
      return response;
    } catch (error) {
      throw new Error(`Failed to retry credentials SMS: ${error.message}`);
    }
  }

  /**
   * Get student by ID
   * @param {string} studentId - Student ID
   * @returns {Promise} API response
   */
  async getStudentById(studentId) {
    try {
      const response = await ApiHelper.get(API_ENDPOINTS.COLLEGES.STUDENT_BY_ID(studentId));
      return response;
    } catch (error) {
      throw new Error(`Failed to fetch student: ${error.message}`);
    }
  }

  /**
   * Update student information
   * @param {string} studentId - Student ID
   * @param {Object} updateData - Data to update
   * @returns {Promise} API response
   */
  async updateStudent(studentId, updateData) {
    try {
      const response = await ApiHelper.put(
        API_ENDPOINTS.COLLEGES.STUDENT_BY_ID(studentId),
        updateData
      );
      return response;
    } catch (error) {
      throw new Error(`Failed to update student: ${error.message}`);
    }
  }

  /**
   * Toggle student status (activate/deactivate)
   * @param {string} studentId - Student ID
   * @param {boolean} isActive - New status
   * @returns {Promise} API response
   */
  async toggleStudentStatus(studentId, isActive) {
    try {
      // Using admin endpoint since COLLEGES might not have direct student status management
      const response = await ApiHelper.patch(
        API_ENDPOINTS.ADMIN.USER_STATUS(studentId),
        { isActive }
      );
      return response;
    } catch (error) {
      throw new Error(`Failed to toggle student status: ${error.message}`);
    }
  }

  /**
   * Search students
   * @param {string} searchTerm - Search query
   * @param {Object} filters - Additional filters
   * @returns {Promise} API response
   */
  async searchStudents(searchTerm, filters = {}) {
    try {
      const params = {
        search: searchTerm,
        ...filters
      };
      const response = await ApiHelper.get(API_ENDPOINTS.COLLEGES.STUDENTS, params);
      return response;
    } catch (error) {
      throw new Error(`Failed to search students: ${error.message}`);
    }
  }

  /**
   * Bulk enroll students in a course
   * @param {Array} studentIds - Array of student IDs
   * @param {string} courseId - Course ID
   * @returns {Promise} API response
   */
  async bulkEnrollStudents(data) {
    try {
      const response = await ApiHelper.post(API_ENDPOINTS.COLLEGES.STUDENTS_BULK_ENROLL, data);
      return response;
    } catch (error) {
      throw new Error(`Failed to bulk enroll students: ${error.message}`);
    }
  }

  /**
   * Delete student (soft delete)
   * @param {string} studentId - Student ID
   * @returns {Promise} API response
   */
  async deleteStudent(studentId) {
    try {
      const response = await ApiHelper.delete(API_ENDPOINTS.COLLEGES.STUDENT_BY_ID(studentId));
      return response;
    } catch (error) {
      throw new Error(`Failed to delete student: ${error.message}`);
    }
  }

  /**
   * Get student enrollment details
   * @param {string} studentId - Student ID
   * @returns {Promise} API response
   */
  async getStudentEnrollments(studentId) {
    try {
      const response = await ApiHelper.get(
        API_ENDPOINTS.COLLEGES.STUDENT_ENROLLMENTS(studentId)
      );
      return response;
    } catch (error) {
      throw new Error(`Failed to fetch student enrollments: ${error.message}`);
    }
  }

  /**
   * Get students by course
   * @param {string} courseId - Course ID
   * @param {Object} params - Query parameters
   * @returns {Promise} API response
   */
  async getStudentsByCourse(courseId, params = {}) {
    try {
      const response = await ApiHelper.get(
        API_ENDPOINTS.COLLEGES.STUDENTS_BY_COURSE(courseId),
        params
      );
      return response;
    } catch (error) {
      throw new Error(`Failed to fetch students by course: ${error.message}`);
    }
  }

  /**
   * Generate student reports
   * @param {Object} filters - Report filters
   * @returns {Promise} API response
   */
  async generateStudentReport(filters = {}) {
    try {
      const response = await ApiHelper.post(API_ENDPOINTS.COLLEGES.STUDENTS_REPORT, filters);
      return response;
    } catch (error) {
      throw new Error(`Failed to generate student report: ${error.message}`);
    }
  }

  async fetchDegreesByCollege(collegeId) {
    console.log('Fetching degrees from helper', collegeId);
    try {
      const response = await ApiHelper.get(
        API_ENDPOINTS.COLLEGES.OFFERED_DEGREES(collegeId)
      );
      return response;
    } catch (error) {
      throw new Error(`Failed to fetch degrees: ${error.message}`);
    }
  }

  /**
   * Import students from other systems
   * @param {Object} importData - Import configuration
   * @returns {Promise} API response
   */
  async importStudents(importData) {
    try {
      const response = await ApiHelper.post(API_ENDPOINTS.COLLEGES.STUDENTS_IMPORT, importData);
      return response;
    } catch (error) {
      throw new Error(`Failed to import students: ${error.message}`);
    }
  }

  /**
   * Export students data
   * @param {Object} exportParams - Export parameters
   * @returns {Promise} Blob response
   */
  async exportStudents(exportParams = {}) {
    try {
      const response = await fetch(API_ENDPOINTS.COLLEGES.STUDENTS_EXPORT, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('authToken')}`,
        },
        body: JSON.stringify(exportParams)
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      return await response.blob();
    } catch (error) {
      throw new Error(`Failed to export students: ${error.message}`);
    }
  }

  /**
   * Get COLLEGES dashboard statistics
   * @returns {Promise} API response
   */
  async getDashboardStats() {
    try {
      const response = await ApiHelper.get(API_ENDPOINTS.COLLEGES.DASHBOARD);
      return response;
    } catch (error) {
      throw new Error(`Failed to fetch dashboard statistics: ${error.message}`);
    }
  }
}

export default new CollegeStudentHelper();